import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { black, grey, white } from "designSystem/styles/color";
import FlexRowCenter from "designSystem/components/flex/FlexRowCenter";
import { DataGridCardProps, Text, Tooltip } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { clickableStyle } from "designSystem/styles/style-functions";
import OverflowText from "designSystem/components/text/OverflowText";
import NodeDataStore from "csi/datastore/NodeDataStore";
import def from "csi/def";
import locale from "csi/locale";
import reflection from "csi/reflection";
import types from "csi/types";
import { C8Node } from "csi/typings";
import { UserAvatar } from "csi/user/UserAvatar";
import { getLocationForUrl } from "csi/utility/routerUtility";
import xml from "csi/xml";
import React, { FunctionComponent } from "react";

/*
ChangeLog Card formatting design/wording logic

Changelog Entry message Format:
Avatar {User} {Timestamp)
{Change Log Operation}{C8 Object Type}{Instance}
{Change Type} {Attribute) from {Old Value} to {New Value} (and made {No more updates}}

For Operation "Update Added/Changed/Removed":
Change Type:
Old Value = Blank, New Value != Blank: Added
Old Value - Blank, New Value != Blank: Changed
Old Value != Blank, New Value = Blank: Rremoved

For Operation "Update Activate/Deactivate":
Change log operation:
Old Value = false, New Value = true: Activate {Instance)
Old Value = true, New Value = false: Deactivate {Instance}

Format for Activate/Deactivate:
{User) (Change Log Operation) {C8 Object Type) (Instance)
Ex: Nichole activate colorway Levender

If attribute is refvector, add/update/remove operation have plural format
Added {valueName} to {attributeName}
Removed {valueName} from {attributeName}

if number of attributes is greater then 1, the rest of attributes are not printed, 
instead adding to the end phrase "and made ${0} more update(s)"
*/

const SP = " ";
const BREADCRUMB_DEPTH = 4;
const ATTR_ACTIVE_NAME = "Active";

interface ChangeLogCardProps extends DataGridCardProps<C8Node> { }

const PREFIX = "ChangeLogCard";

const classes = {
	clickable: `${PREFIX}clickable`,
	image40: `${PREFIX}image40`,
	infoPane: `${PREFIX}infoPane`,
	dateChanged: `${PREFIX}dateChanged`,
	secondRow: `${PREFIX}secondRow`,
	operation: `${PREFIX}operation`,
	instanceLink: `${PREFIX}instanceLink`,
	thirdRow: `${PREFIX}thirdRow`,
	change: `${PREFIX}change`,
	valueName: `${PREFIX}valueName`,
	fourthRow: `${PREFIX}fourthRow`,
	iconInfo: `${PREFIX}iconInfo`,
};

const Root = styled("div")((
	{
		theme,
	},
) => ({
	padding: 12,
	borderBottom: "1px solid rgba(0, 0, 0, .05)",
	boxSizing: "border-box",
	display: "flex",
	flexDirection: "row",
	background: white[50],
	[`&.${classes.clickable}`]: clickableStyle(theme),
	[`& .${classes.image40}`]: {
		maxHeight: 40,
		maxWidth: 40,
		height: 40,
		width: 40,
		marginRight: 12,
		display: "flex",
		alignItems: "center",
	},
	[`& .${classes.infoPane}`]: {
		display: "flex",
		flexDirection: "column",
		width: 395,
		"& span": {
			height: 18,
		},
	},
	[`& .${classes.dateChanged}`]: {
		fontSize: 10,
		verticalAlign: "middle",
		color: grey[800],
		padding: "0 12px",
		fontWeight: 600,
	},
	[`& .${classes.secondRow}`]: {
		marginTop: 6,
		display: "flex",
	},
	[`& .${classes.operation}`]: {
		color: black[100],
		fontSize: 12,
		fontWeight: "bold",
		overflow: "hidden",
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: 2,
	},
	[`& .${classes.instanceLink}`]: {
		fontStyle: "italic",
	},
	[`& .${classes.thirdRow}`]: {
		display: "flex",
	},
	[`& .${classes.change}`]: {
		color: black[100],
		fontSize: 12,
		overflow: "hidden",
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: 3,
	},
	[`& .${classes.valueName}`]: {
		fontStyle: "italic",
		color: black[500],
	},
	[`& .${classes.fourthRow}`]: {
		marginTop: 4,
		color: grey[800],
	},
	[`& .${classes.iconInfo}`]: {
		color: grey[800],
		marginLeft: 4,
	},
}));

export const ChangeLogCard: FunctionComponent<ChangeLogCardProps> = (props) => {
	const { item, store } = props;
	const { cache } = store as NodeDataStore;
	const loc = useLocale();

	function getNodeDisplayName(node: any) {
		return locale.getNodeReferenceName(node, cache);
	}

	function isRevision(node: any) {
		const rc = reflection.getClass(node.ActualType);
		return rc && rc.isRevision();
	}

	function isEmptyOrNull(value: any) {
		if (typeof value == "boolean") {
			return false;
		}
		return (value == null) || xml.isEmptyURL(value);
	}

	function getRevisionDisplayName(name: string) {
		return `[${loc.resource("RevisionTitle", [name])}]`;
	}

	function addQuotes(value: string) {
		return `"${value}"`;
	}

	function getLocalizedEnumName(value: string) {
		const enumName = locale.getEnumName(value);
		return enumName || locale.resource("NAME_EMPTY");
	}

	function getValueName(value: any, type?: string) {
		if (xml.isValidURL(value)) {
			const node = cache.get(value);
			if (!node) {
				return "";
			}
			const name = getNodeDisplayName(node);
			return isRevision(node) ? getRevisionDisplayName(name) : addQuotes(name);
		}

		if (type == xml.ATTR_ENUM) {
			return getLocalizedEnumName(value);
		}

		if (type == xml.ATTR_TIME) {
			return types.formatTime(value, type);
		}

		return addQuotes(value);
	}

	function capitalizeFirstLetter(string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	/**
	 * Creates breadcrumb as deep as BREADCRUMB_DEPTH (used for context column tooltip)
	 * @param {Object} node - result node
	 * return {string}
	 */
	function createCrumb(node: C8Node) {
		let level = 0;
		let crumb = "";
		while ((level <= BREADCRUMB_DEPTH) && node) {
			if (isRevision(node)) {
				if (level) { // Don't show instance node in tooltip breadcrumb (level == 0 for instance node)
					const txt = loc.resource("Revision") + " " + getNodeDisplayName(node);
					crumb += txt;
				}
				node = cache.get(node.ParentContext);
				console.assert(!!node, "Change Log Context: revision has no parent");
			}
			if (level) {
				crumb = getNodeDisplayName(node) + (crumb ? " / " : "") + crumb;
			}
			level++;
			node = node && cache.get(node.ParentContext);
		}
		return crumb;
	}


	const author = cache.get(item.CurrentUser);
	const actualAuthor = cache.get(author.ActualNode) || author;
	const authorName = getNodeDisplayName(actualAuthor);
	const changeDate = types.formatTime(item.ChangeTime, types.FORMAT_DATETIME);

	// get c8instance
	const instanceNode = cache.get(item.OriginalContext);
	const actualType = instanceNode.ActualType;
	const instanceType = loc.type(actualType);
	const instanceName = addQuotes(getNodeDisplayName(instanceNode));
	const instanceHref = (!instanceNode.Deleted && def.canNavigateToType(actualType)) ? <a href={getLocationForUrl(item.DomainInstance)}>{instanceName}</a> : instanceName;

	// breadcrumb
	const crumb = createCrumb(instanceNode);
	const context = getNodeDisplayName(cache.get(instanceNode.ParentContext));

	// get operation
	const op = item.Operation;
	const operation = getLocalizedEnumName(op);
	const opText = operation + SP + instanceType + SP;

	// changed attribue
	const attrNum = item.Attributes.length;
	const moreUpdatesCount = attrNum - 1;
	const bMoreUpdates = moreUpdatesCount > 0;
	const attribute = attrNum ? cache.get(item.Attributes[0]) : null;

	// block of variables which will be defined if "attribute" exists on item
	let oldValueName = "";
	let bPrintOldValueName = false;
	let changedTo = "";
	let bChangedTo = false;
	let valueName: any = "";
	let newValue = null;
	let change = "";
	let changePlural = "";
	let bPrintValueName = false;
	let bChangePlural = false;
	let bActivate = false;
	let bLink = false;

	// attribute might be undefined in log entry about given object removal
	if (attribute) {
		// old Attribute value
		let oldValue = attribute.OldValue;
		const attrType = attribute.AttributeType;
		const attId = attribute.$Name;
		const attrName = locale.typeId(actualType, getNodeDisplayName(attribute));
		if (Array.isArray(oldValue)) {
			oldValue = oldValue.length ? oldValue[0] : null;
		}
		oldValueName = getValueName(oldValue, attrType);

		// new attribute value
		newValue = attribute.NewValue;
		if (Array.isArray(newValue)) {
			newValue = newValue.length ? newValue[0] : null;
		}
		valueName = getValueName(newValue, attrType);
		const newValueNode = cache.get(newValue);
		if (newValueNode) {
			const attrType = newValueNode.ActualType;
			if (def.canNavigateToType(attrType)) {
				valueName = <a className={classes.instanceLink} href={getLocationForUrl(newValueNode.ActualNode)}> {valueName}</a>;
				bLink = true;
			}
		}

		// special op name for attribute "Activate" - "activate/deactivate"
		if (attId == ATTR_ACTIVE_NAME) {
			if (((oldValue === false) || (oldValue === undefined)) && (newValue === true)) {
				bActivate = true;
				change = loc.resource("ChangeLogActivate", [instanceType]) + SP;
			} else if (((oldValue === true) || (oldValue === undefined)) && (newValue === false)) {
				bActivate = true;
				change = locale.resource("ChangeLogDeactivate", [instanceType]) + SP;
			}
		} else if (isEmptyOrNull(oldValue) && (newValue != null)) {
			if (attribute.AttributeType == xml.ATTR_REFLIST) {
				change = loc.resource("ChangeLogAdded") + SP;
				changePlural = SP + loc.resource("ChangeLogAddedTo") + SP + attrName + SP;
				bChangePlural = true;
			} else {
				change = loc.resource("ChangeLogAdded") + SP + attrName + SP;
			}
			bPrintValueName = true;
		} else if ((oldValue != null) && (newValue != null)) {
			change = loc.resource("ChangeLogChanged") + SP + attrName + SP + loc.resource("ChangedFrom") + SP;
			changedTo = SP + loc.resource("ChangedTo") + SP;
			bChangedTo = true;
			bPrintOldValueName = true;
			bPrintValueName = true;
		} else if ((oldValue != null) && isEmptyOrNull(newValue)) {
			if (attribute.AttributeType == xml.ATTR_REFLIST) {
				change = loc.resource("ChangeLogRemoved") + SP;
				changePlural = SP + loc.resource("ChangeLogRemovedFrom") + SP + attrName + SP;
				bChangePlural = true;
				valueName = oldValueName;
				bLink = false;
				bPrintValueName = true;
			} else {
				change = loc.resource("ChangeLogRemoved") + SP + attrName + SP;
				bPrintOldValueName = true;
			}
		}
		change = capitalizeFirstLetter(change);
	}

	return (
		<Root className={classes.clickable}>
			<UserAvatar className={classes.image40} user={actualAuthor} />
			<div className={classes.infoPane}>
				<div>
					<Text color="textPrimary" text={authorName} />
					<span className={classes.dateChanged}>{changeDate}</span>
				</div>
				{!bActivate && (
					<div className={classes.secondRow}>
						<div className={classes.operation}>{opText}{instanceHref}</div>
					</div>
				)}
				{attribute && (
					<div className={classes.thirdRow}>
						<div className={`${classes.change}` + (bActivate ? ` ${classes.operation}` : "")}>
							{change}
							{bPrintOldValueName && (<span className={classes.valueName}>{oldValueName}</span>)}
							{bChangedTo && changedTo}
							{bActivate && instanceHref}
							{bPrintValueName && (bLink ? valueName : <span className={classes.valueName}>{valueName}</span>)}
							{bChangePlural && changePlural}
							{bMoreUpdates && (SP + loc.resource("ChangedMore", [moreUpdatesCount]))}
						</div>
					</div>
				)}
				<FlexRowCenter className={classes.fourthRow}>
					<OverflowText lineHeight={16} text={context} />
					<Tooltip placement="top" title={crumb}>
						<InfoOutlinedIcon className={classes.iconInfo} />
					</Tooltip>
				</FlexRowCenter>
			</div>
		</Root>
	);
};